import type { FormInstance } from 'antd';
import type { StoreApi } from 'zustand';

import { sendEmailConfirmation } from '~/services/auth/AuthService';
import type { IEmailConfirmationParams } from '~/types/api/Auth';
import type { IAuthStore } from './useAuthStore';

export type IEmailConfirmationFormFilter = {
  email: string;
};

export type IEmailConfirmationActions = {
  resetStates: () => void;
  setFormFilterInstance: (form: Partial<FormInstance<IEmailConfirmationFormFilter>>) => void;
  getParams: () => IEmailConfirmationParams;
  handleSubmit: () => Promise<void>;
};

export type IEmailConfirmationState = {
  isLoading: boolean;
  formFilterInstance: Partial<FormInstance<IEmailConfirmationFormFilter>> | null;
};

export type IEmailConfirmationSlice = IEmailConfirmationState & IEmailConfirmationActions;

const useEmailConfirmationSlice = (
  set: StoreApi<IAuthStore>['setState'],
  get: StoreApi<IAuthStore>['getState'],
): IEmailConfirmationSlice => ({
  // States
  isLoading: false,
  formFilterInstance: null,

  // Setters
  setFormFilterInstance: form => {
    get().setData(state => {
      state.emailConfirmationSlice.formFilterInstance = form;
    });
  },

  // Actions
  resetStates: () => {
    get().setData(state => {
      state.emailConfirmationSlice.isLoading = false;
      state.emailConfirmationSlice.formFilterInstance = null;
    });
  },
  getParams: () => {
    const { formFilterInstance } = get().emailConfirmationSlice;
    if (!formFilterInstance?.getFieldsValue) {
      throw new Error('Instância do formulário não está definida.');
    }

    const formValues = formFilterInstance.getFieldsValue();

    return {
      email: formValues.email,
    } as IEmailConfirmationParams;
  },
  handleSubmit: async () => {
    const { formFilterInstance } = get().emailConfirmationSlice;
    if (!formFilterInstance) return;
    get().setData(state => {
      state.emailConfirmationSlice.isLoading = true;
    });

    try {
      const params = get().emailConfirmationSlice.getParams();
      await sendEmailConfirmation(params);
    } catch (error) {
      return Promise.reject(error);
    } finally {
      get().setData(state => {
        state.emailConfirmationSlice.isLoading = false;
      });
    }
  },
});

export default useEmailConfirmationSlice;
